import { createAsyncThunk, createSlice, current } from "@reduxjs/toolkit";
import { CaseActivities } from "../../types/CasesActivities";
import { isEqual } from "lodash";
import api from "../../utils/api";

interface ChatState {
  chats: CaseActivities[];
  isLoading: boolean;
  error: string | null;
  unseen: number;
}

const initialState: ChatState = {
  chats: [],
  isLoading: false,
  error: null,
  unseen: 0,
};

export const addNewMessage = createAsyncThunk(
  "chatSystem/addNewMessage",
  async (
    {
      case_id,
      user_id,
      content,
      type,
      by,
    }: {
      case_id: string;
      user_id: number;
      content: string;
      type: string;
      by: string;
    },
    { rejectWithValue },
  ) => {
    try {
      const res = await api.post(
        `/auth/chat/messages/add`,
        { case_id, user_id, content, type, by },
      );
      return res?.data?.data;
    } catch (error: any) {
      return rejectWithValue(error?.response?.data?.message);
    }
  },
);

export const fetchMessages = createAsyncThunk(
  "chatSystem/fetchMessages",
  async (
    { case_id, userId }: { case_id: string; userId: string },
    { rejectWithValue },
  ) => {
    try {
      const res = await api.get(
        `/auth/chat/messages/${case_id}/${userId}`,
      );
      return res?.data?.data;
    } catch (error: any) {
      return rejectWithValue(error?.response?.data?.message);
    }
  },
);

export const updateReplied = createAsyncThunk(
  "chatSystem/updateReplied",
  async (
    {
      case_id,
      replied,
      userId,
    }: {
      case_id: string;
      replied: boolean;
      userId: number;
    },
    { rejectWithValue },
  ) => {
    try {
      await api.put(
        `/auth/chat/messages/update/${case_id}`,
        { replied, userId },
      );
      return { case_id, replied, userId };
    } catch (error: any) {
      return rejectWithValue(error?.response?.data?.message);
    }
  },
);

const CasesActivities = createSlice({
  name: "adminChat",
  initialState,
  reducers: {
    clearChats: (state) => {
      state.chats = [];
      state.error = null;
      state.unseen = 0;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(addNewMessage.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(addNewMessage.fulfilled, (state, action) => {
        state.isLoading = false;
        if (action.payload) {
          state.chats.push(action.payload);
        }
      })
      .addCase(addNewMessage.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })

      .addCase(fetchMessages.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchMessages.fulfilled, (state, action) => {
        state.isLoading = false;

        const newChats = Array.isArray(action.payload) ? action.payload : [];
        // Unwrap Immer proxy before lodash isEqual (proxy breaks prototype access)
        if (!isEqual(current(state.chats), newChats)) {
          state.chats = newChats;
          state.unseen = newChats.filter(
            (chat) => chat.replied === false,
          ).length;
        }
      })
      .addCase(fetchMessages.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })

      .addCase(updateReplied.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateReplied.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(updateReplied.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearChats } = CasesActivities.actions;
export default CasesActivities.reducer;